// ─────────────────────────────────────────────────────────────
//  MonteCarloChart  – Simulation output distribution
//  Runs N iterations client-side, renders histogram + percentiles
// ─────────────────────────────────────────────────────────────
import React, { useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, Cell,
} from "recharts";

// ── Monte Carlo engine ─────────────────────────────────────────
function runMonteCarlo({ baseRevenue, baseExpense, revenueStdPct, expenseStdPct, iterations = 5000 }) {
  const rand = () => {
    // Box-Muller transform → standard normal
    const u1 = Math.random(), u2 = Math.random();
    return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  };

  const results = [];
  for (let i = 0; i < iterations; i++) {
    const rev  = baseRevenue  * (1 + (revenueStdPct  / 100) * rand());
    const exp  = baseExpense  * (1 + (expenseStdPct  / 100) * rand());
    results.push(rev - exp);
  }
  results.sort((a, b) => a - b);
  return results;
}

function buildHistogram(values, bins = 40) {
  const min = values[0], max = values[values.length - 1];
  const step = (max - min) / bins;
  const buckets = Array.from({ length: bins }, (_, i) => ({
    x: min + i * step,
    count: 0,
  }));
  values.forEach((v) => {
    const idx = Math.min(Math.floor((v - min) / step), bins - 1);
    buckets[idx].count++;
  });
  return buckets;
}

function percentile(arr, p) {
  const idx = Math.floor((p / 100) * arr.length);
  return arr[idx];
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { x, count } = payload[0].payload;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e0e6f0",
      borderRadius: 8, padding: "8px 14px", fontSize: 12,
      boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    }}>
      <div style={{ fontWeight: 700, color: "#1a2340" }}>
        ₹{(x / 100000).toFixed(1)}L – ₹{((x + (payload[0]?.payload?.step || 0)) / 100000).toFixed(1)}L
      </div>
      <div style={{ color: "#4969ab" }}>Frequency: {count}</div>
    </div>
  );
};

export default function MonteCarloChart({
  baseRevenue    = 10000000,
  baseExpense    = 7500000,
  revenueStdPct  = 15,
  expenseStdPct  = 10,
  iterations     = 5000,
  height         = 300,
  title          = "Monte Carlo Profit Simulation",
}) {
  const { histogram, p10, p50, p90, mean } = useMemo(() => {
    const results   = runMonteCarlo({ baseRevenue, baseExpense, revenueStdPct, expenseStdPct, iterations });
    const histogram = buildHistogram(results, 40);
    return {
      histogram,
      p10:  percentile(results, 10),
      p50:  percentile(results, 50),
      p90:  percentile(results, 90),
      mean: results.reduce((a, b) => a + b, 0) / results.length,
    };
  }, [baseRevenue, baseExpense, revenueStdPct, expenseStdPct, iterations]);

  const fmt = (v) => `₹${(v / 100000).toFixed(1)}L`;

  return (
    <div style={{
      background: "#fff", borderRadius: 12,
      boxShadow: "0 2px 12px rgba(73,105,171,0.08)",
      padding: "18px 14px",
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#1a2340", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 11, color: "#aaa", marginBottom: 10 }}>
        {iterations.toLocaleString()} simulations · Revenue σ={revenueStdPct}% · Expense σ={expenseStdPct}%
      </div>

      {/* Percentile summary pills */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          { label: "P10 (Pessimistic)", value: p10, color: "#c0392b" },
          { label: "P50 (Median)",      value: p50, color: "#4969ab" },
          { label: "Mean",              value: mean, color: "#2d8c4e" },
          { label: "P90 (Optimistic)",  value: p90, color: "#27ae60" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{
            background: `${color}12`, border: `1px solid ${color}40`,
            borderRadius: 8, padding: "6px 12px", fontSize: 11,
          }}>
            <div style={{ color: "#888", marginBottom: 2 }}>{label}</div>
            <div style={{ fontWeight: 800, color, fontSize: 14 }}>{fmt(value)}</div>
          </div>
        ))}
      </div>

      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={histogram} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" vertical={false} />
          <XAxis
            dataKey="x"
            tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`}
            tick={{ fontSize: 9, fill: "#888" }}
            interval={Math.floor(histogram.length / 6)}
          />
          <YAxis tick={{ fontSize: 10, fill: "#888" }} />
          <Tooltip content={<CustomTooltip />} />

          <ReferenceLine x={p10}  stroke="#c0392b" strokeDasharray="4 3"
            label={{ value: "P10", position: "top", fontSize: 9, fill: "#c0392b" }} />
          <ReferenceLine x={mean} stroke="#2d8c4e" strokeDasharray="4 3"
            label={{ value: "Mean", position: "top", fontSize: 9, fill: "#2d8c4e" }} />
          <ReferenceLine x={p90}  stroke="#27ae60" strokeDasharray="4 3"
            label={{ value: "P90", position: "top", fontSize: 9, fill: "#27ae60" }} />

          <Bar dataKey="count" radius={[2, 2, 0, 0]}>
            {histogram.map((entry, i) => (
              <Cell
                key={i}
                fill={
                  entry.x < p10  ? "#c0392b" :
                  entry.x < p50  ? "#e67e22" :
                  entry.x < mean ? "#4969ab" :
                  entry.x < p90  ? "#2d8c4e" : "#27ae60"
                }
                opacity={0.8}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <div style={{ fontSize: 10, color: "#bbb", marginTop: 8, textAlign: "center" }}>
        Distribution of simulated profit outcomes. Green zone = above mean. Red zone = pessimistic tail.
      </div>
    </div>
  );
}
